var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, FieldWrapper, FlexBox, FormField, FormGrid, Icon, InputWrapper, Page, } from '@dixa/lib-shared-habla';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce, useUpdateEffect } from 'react-use';
import { billingApi } from 'apis/billing';
import { countries } from 'app/utils/countries/countries';
import { Sentry } from 'app/utils/sentry';
import { HookFormText } from 'components/hookForm';
import { useNotifications } from 'hooks/useNotifications';
import styles from './billingInformationSection.module.scss';
import { BillingInformation } from './components/billingInformation';
const formId = 'billing-information-form';
export const BillingInformationSection = () => {
    var _a, _b, _c;
    const notify = useNotifications();
    const [isEditable, setIsEditable] = useState(false);
    const { paymentInformation, isFetching: isPaymentInformationFetching } = billingApi.useReadPaymentInformationQuery(undefined, {
        selectFromResult: (_a) => {
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { paymentInformation: data === null || data === void 0 ? void 0 : data.data.payment_information }));
        },
    });
    const defaultValues = useMemo(() => {
        if (!paymentInformation)
            return;
        const keys = [
            'name',
            'company_name',
            'country',
            'state',
            'email',
            'address1',
            'address2',
            'postal_code',
            'city',
            'vatin',
        ];
        return keys.reduce((acc, key) => { var _a; return (Object.assign(Object.assign({}, acc), { [key]: (_a = paymentInformation[key]) !== null && _a !== void 0 ? _a : '' })); }, {});
    }, [paymentInformation]);
    const [updatePaymentInformation, { isLoading: isPaymentInformationUpdating },] = billingApi.useUpdatePaymentInformationMutation();
    const [readValidateVatNumber] = billingApi.useLazyReadValidateVatNumberQuery();
    const isPaymentInformationFormDisabled = isPaymentInformationFetching || isPaymentInformationUpdating;
    const { handleSubmit, control, reset, formState: { errors, touchedFields }, register, setError, clearErrors, setFocus, watch, } = useForm({
        defaultValues,
    });
    const watchedVatin = watch('vatin');
    const validateVatinFormField = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!touchedFields.vatin)
            return;
        try {
            const { data } = yield readValidateVatNumber({
                vatNumber: watchedVatin,
            }).unwrap();
            if (!data.result) {
                return setError('vatin', {
                    message: 'The VAT ID number is invalid, please use the format DK99999999',
                    type: 'value',
                });
            }
            return clearErrors('vatin');
        }
        catch (error) {
            Sentry.captureException(error);
        }
    });
    const [_, cancelDebouncedValidateVatinFormField] = useDebounce(validateVatinFormField, 300, [watchedVatin, touchedFields.vatin]);
    useUpdateEffect(() => {
        cancelDebouncedValidateVatinFormField();
        reset(defaultValues);
    }, [defaultValues, reset]);
    const handleOnSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        const { updates, deletes } = Object.keys(values).reduce((acc, key) => {
            if (values[key] === '') {
                acc.deletes.push(key);
            }
            else {
                acc.updates[key] = values[key];
            }
            return acc;
        }, { updates: {}, deletes: [] });
        try {
            const { data } = yield updatePaymentInformation({
                patch: {
                    updates,
                    deletes,
                },
            }).unwrap();
            if (Object.keys(data.update_errors).length) {
                throw data.update_errors;
            }
            reset(values);
            setIsEditable(false);
            notify({
                title: 'Successfully updated billing information',
                icon: 'done',
                className: 'success',
                time: 3000,
                showDesktop: false,
            });
        }
        catch (error) {
            const errorMessage = error.InvalidType
                ? error.InvalidType
                : `Please try again or contact support.`;
            if (errorMessage.includes('VATIN')) {
                setFocus('vatin');
                validateVatinFormField();
            }
            notify({
                title: 'Failed to update update billing information',
                text: errorMessage,
                icon: 'exclamation',
                className: 'danger',
                time: 5000,
                showDesktop: false,
            });
        }
    });
    return (_jsx(Page.Section, Object.assign({ title: "Billing information", controls: _jsxs(_Fragment, { children: [isEditable && (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ variant: "secondary", onClick: () => {
                                reset(defaultValues);
                                setIsEditable(false);
                            } }, { children: "Cancel" })), _jsx(Button, Object.assign({ variant: "primary", type: "submit", form: formId, isLoading: isPaymentInformationUpdating }, { children: "Save details" }))] })), !isEditable && (_jsx(Button, Object.assign({ variant: "secondary", onClick: () => setIsEditable(true) }, { children: "Edit" })))] }) }, { children: _jsxs(Box, Object.assign({ className: styles.paymentInformation, minHeight: "170px" }, { children: [!isEditable && (_jsx(BillingInformation, { name: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.name, address1: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.address1, address2: (_a = paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.address2) !== null && _a !== void 0 ? _a : '', city: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.city, companyName: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.company_name, country: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.country, email: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.email, postalCode: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.postal_code, state: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.state, vatin: paymentInformation === null || paymentInformation === void 0 ? void 0 : paymentInformation.vatin, isLoading: isPaymentInformationFetching })), isEditable && (_jsx(Box, Object.assign({ padding: "300 0 0 0" }, { children: _jsx("form", Object.assign({ id: formId, onSubmit: handleSubmit(handleOnSubmit), noValidate: true }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "name", label: "Name", control: control, disabled: isPaymentInformationFormDisabled, rules: {
                                            required: 'Name is required',
                                        } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "email", label: "Email", control: control, disabled: isPaymentInformationFormDisabled, helpIcon: {
                                            text: 'The email address to receive invoices',
                                            dark: true,
                                        }, rules: {
                                            required: 'Billing email is required',
                                            pattern: {
                                                value: /\S+@\S+\.\S+/,
                                                message: 'Entered value does not match email format',
                                            },
                                        } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "company_name", label: "Company name", control: control, disabled: isPaymentInformationFormDisabled, rules: {
                                            required: 'Company name is required',
                                        } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "vatin", label: "VAT ID number", control: control, disabled: isPaymentInformationFormDisabled, helpIcon: {
                                            text: 'VAT ID number is used in many countries for tax purposes. Please provide your VAT ID number if you are purchasing Dixa for a business purpose and is EU based.',
                                            dark: true,
                                        }, componentProps: {
                                            placeholder: 'VAT ID number... (eg. DK99999999)',
                                        } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "address1", label: "Address (P.O. box, company name, c/o) ", control: control, disabled: isPaymentInformationFormDisabled, rules: {
                                            required: 'Address is required',
                                        } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "address2", label: "Address line 2 (Apartment, suite, unit)", control: control, disabled: isPaymentInformationFormDisabled }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "city", label: "City", control: control, disabled: isPaymentInformationFormDisabled, rules: {
                                            required: 'City is required',
                                        } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "postal_code", label: "Postal/Zip code", control: control, disabled: isPaymentInformationFormDisabled, rules: {
                                            required: 'Zip code is required',
                                        } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(FieldWrapper, Object.assign({ label: "Country", required: true, disabled: isPaymentInformationFormDisabled, meta: {
                                            error: (_b = errors.country) === null || _b === void 0 ? void 0 : _b.message,
                                            valid: !((_c = errors.country) === null || _c === void 0 ? void 0 : _c.message),
                                            touched: !!touchedFields.country,
                                        } }, { children: _jsxs(InputWrapper, Object.assign({ className: styles.selectWrapper }, { children: [_jsxs("select", Object.assign({ className: styles.select }, register('country', {
                                                    required: 'Country is required',
                                                    disabled: isPaymentInformationFormDisabled,
                                                }), { children: [_jsx("option", Object.assign({ value: "", defaultChecked: true }, { children: "Choose your country" })), countries.map((item) => (_jsx("option", Object.assign({ value: item.countryCode }, { children: item.name }), item.countryCode)))] })), _jsx(FlexBox, Object.assign({ className: styles.selectWrapperIconWrapper, width: "24px", height: "24px", alignItems: "center", justifyContent: "center" }, { children: _jsx(Icon, { name: "angle-down", color: "tertiary" }) }))] })) })) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "state", label: "State/Province", control: control, disabled: isPaymentInformationFormDisabled }) }))] }) })) })))] })) })));
};
